import { computed, reactive } from 'vue'
import { defineStore } from 'pinia'
import { ApiRequestError, getCurrentAccount, setAuthFailureHandler } from '@/api/client'
import type { Account } from '@/api/schemas'

type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'anonymous'

const state = reactive<{
  account: Account | null
  status: AuthStatus
  restored: boolean
  forbidden: boolean
}>({
  account: null,
  status: 'idle',
  restored: false,
  forbidden: false,
})

let restorePromise: Promise<Account | null> | undefined

function clearSession() {
  state.account = null
  state.status = 'anonymous'
}

setAuthFailureHandler((status) => {
  if (status === 401) clearSession()
  if (status === 403) state.forbidden = true
  if (typeof window !== 'undefined')
    window.dispatchEvent(new CustomEvent('ccd-auth-failure', { detail: status }))
})

export async function restoreSession(): Promise<Account | null> {
  if (state.restored) return state.account
  if (restorePromise) return restorePromise
  state.status = 'loading'
  restorePromise = getCurrentAccount()
    .then((account) => {
      state.account = account
      state.status = 'authenticated'
      return account
    })
    .catch((error: unknown) => {
      if (!(error instanceof ApiRequestError) || error.status !== 401) throw error
      clearSession()
      return null
    })
    .then((account) => {
      state.restored = true
      return account
    })
    .finally(() => {
      restorePromise = undefined
    })
  return restorePromise
}

function setAccount(account: Account) {
  state.account = account
  state.status = 'authenticated'
  state.restored = true
  state.forbidden = false
}

function clearAccount() {
  clearSession()
  state.restored = true
}

export const useAuthStore = defineStore('auth', () => ({
  state,
  account: computed(() => state.account),
  isAdmin: computed(() => state.account?.permissions.includes('intakes:read') ?? false),
  hasPermission: (permission: string) => state.account?.permissions.includes(permission) ?? false,
  restoreSession,
  setAccount,
  clearAccount,
}))
